import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {FileSizeFormatter} from '../../../../srcCommon/formatters/fileSizeFormatter';
import {AnalyticsExportModel, AsyncExportTypesEnum} from '../../../models/api/analyticsExportModel';
import {downloadFileAsync} from '../../../services/browser';

/*
 * Constants.
 */

const EXPORT_DONE_STATUS = 'done';

/*
 * Props.
 */

interface AnalyticsExportDialogItemProps {
  exportItem: AnalyticsExportModel;
}

/*
 * Style.
 */

const StyledExportDiv = styled.div`
  position: relative;
  padding: 5px;
  background: #f8f8f8;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  font-size: 12px;

  margin-bottom: 3px;
`;

const StyledExportTypeSpan = styled.span`
  text-transform: capitalize;
`;

const StyledProgressDiv = styled.div`
  float: right;
  text-transform: capitalize;
`;

const StyledDownloadDiv = styled.div`
  position: relative;
  float: right;
  margin-top: -5px;
`;

/*
 * Components.
 */

export const AnalyticsExportDialogItem: FC<AnalyticsExportDialogItemProps> = ({exportItem}) => {
  const onDownloadClick = async () =>
    await downloadFileAsync(exportItem.downloadUrl, exportItem.filename, {shouldPromptForPath: true});

  return (
    <StyledExportDiv key={exportItem.filename}>
      {renderExportPeriod(exportItem)} {renderExportName(exportItem.exportType)}{' '}
      {renderFileSize(exportItem.size)}
      {renderProgressOrDownload(exportItem, onDownloadClick)}
    </StyledExportDiv>
  );
};

/*
 * Helpers.
 */

function renderExportPeriod(exportItem: AnalyticsExportModel) {
  return (
    <>
      {renderDate(exportItem.start)} &#8594; {renderDate(exportItem.end)}
    </>
  );
}

function renderExportName(exportType: string) {
  switch (exportType) {
    case AsyncExportTypesEnum.CSAT:
      return <Trans id="analytics-export-dialog-csat-export-name">Customer satisfaction export</Trans>;

    default:
      return (
        <Trans id="analytics-export-dialog-default-export-name">
          <StyledExportTypeSpan>{exportType}</StyledExportTypeSpan> export
        </Trans>
      );
  }
}

function renderDate(dateMillis: number) {
  const date = DateTime.fromMillis(dateMillis);
  return date.toLocaleString(DateTime.DATE_SHORT);
}

function renderFileSize(fileSize: number | null) {
  if (!fileSize) {
    return '';
  }

  return <FileSizeFormatter value={fileSize} />;
}

function renderProgressOrDownload(exportItem: AnalyticsExportModel, onDownloadClick: Handler) {
  if (exportItem.status === EXPORT_DONE_STATUS) {
    return (
      <StyledDownloadDiv>
        <IconButton iconName="downloadSmall" label="Download export" onClick={onDownloadClick} />
      </StyledDownloadDiv>
    );
  }

  if (exportItem.progress === 0) {
    return <StyledProgressDiv>{exportItem.status}</StyledProgressDiv>;
  }

  return <StyledProgressDiv>{exportItem.progress}%</StyledProgressDiv>;
}
